/**
 * Canonical BCP-47 language tags — single source of truth (ENG-1067).
 *
 * The standard: every supported language is region-tagged — `lang-REGION`, or `lang-Script-REGION`
 * for script languages (Chinese). The default region for a bare/legacy code is the CLDR likely-subtags
 * region (`Intl.Locale(code).maximize()`). One canonical tag per language, no bare codes.
 *
 * Imported by the language catalog/picker, write-side validation, the surveys runtime resolver, and
 * the data migration so all four agree on one tag per language. Generated from the survey-language
 * catalog plus observed production codes; `normalizeLanguageCode` falls back to CLDR for anything not
 * in the table, so unknown inputs still resolve.
 */

/** Every known input code (catalog + legacy/production spellings) to its canonical region-tagged BCP-47 tag. */
export const LANGUAGE_CANONICAL_MAP: Readonly<Record<string, string>> = {
  aa: "aa-ET",
  "aa-ET": "aa-ET",
  ab: "ab-GE",
  "ab-GE": "ab-GE",
  ae: "ae-IR",
  "ae-IR": "ae-IR",
  af: "af-ZA",
  "af-ZA": "af-ZA",
  ak: "ak-GH",
  "ak-GH": "ak-GH",
  am: "am-ET",
  "am-ET": "am-ET",
  an: "an-ES",
  "an-ES": "an-ES",
  ar: "ar-EG",
  "ar-AE": "ar-AE",
  "ar-EG": "ar-EG",
  "ar-MA": "ar-MA",
  "ar-SA": "ar-SA",
  as: "as-IN",
  "as-IN": "as-IN",
  av: "av-RU",
  "av-RU": "av-RU",
  ay: "ay-BO",
  "ay-BO": "ay-BO",
  az: "az-AZ",
  "az-AZ": "az-AZ",
  ba: "ba-RU",
  "ba-RU": "ba-RU",
  be: "be-BY",
  "be-BY": "be-BY",
  bg: "bg-BG",
  "bg-BG": "bg-BG",
  bh: "bho-IN",
  "bho-IN": "bho-IN",
  bi: "bi-VU",
  "bi-VU": "bi-VU",
  bm: "bm-ML",
  "bm-ML": "bm-ML",
  bn: "bn-BD",
  "bn-BD": "bn-BD",
  bo: "bo-CN",
  "bo-CN": "bo-CN",
  br: "br-FR",
  "br-FR": "br-FR",
  bs: "bs-BA",
  "bs-BA": "bs-BA",
  ca: "ca-ES",
  "ca-ES": "ca-ES",
  ce: "ce-RU",
  "ce-RU": "ce-RU",
  ch: "ch-GU",
  "ch-GU": "ch-GU",
  co: "co-FR",
  "co-FR": "co-FR",
  cr: "cr-CA",
  "cr-CA": "cr-CA",
  cs: "cs-CZ",
  "cs-CZ": "cs-CZ",
  cu: "cu-RU",
  "cu-RU": "cu-RU",
  cv: "cv-RU",
  "cv-RU": "cv-RU",
  cy: "cy-GB",
  "cy-GB": "cy-GB",
  da: "da-DK",
  "da-DK": "da-DK",
  de: "de-DE",
  "de-AT": "de-AT",
  "de-CH": "de-CH",
  "de-DE": "de-DE",
  dv: "dv-MV",
  "dv-MV": "dv-MV",
  dz: "dz-BT",
  "dz-BT": "dz-BT",
  ee: "ee-GH",
  "ee-GH": "ee-GH",
  el: "el-GR",
  "el-GR": "el-GR",
  en: "en-US",
  "en-AU": "en-AU",
  "en-CA": "en-CA",
  "en-GB": "en-GB",
  "en-IE": "en-IE",
  "en-US": "en-US",
  eo: "eo-001",
  "eo-001": "eo-001",
  es: "es-ES",
  "es-AR": "es-AR",
  "es-CL": "es-CL",
  "es-CO": "es-CO",
  "es-ES": "es-ES",
  "es-MX": "es-MX",
  "es-PE": "es-PE",
  "es-VE": "es-VE",
  et: "et-EE",
  "et-EE": "et-EE",
  eu: "eu-ES",
  "eu-ES": "eu-ES",
  fa: "fa-IR",
  "fa-IR": "fa-IR",
  ff: "ff-SN",
  "ff-SN": "ff-SN",
  fi: "fi-FI",
  "fi-FI": "fi-FI",
  "fil-PH": "fil-PH",
  fj: "fj-FJ",
  "fj-FJ": "fj-FJ",
  fo: "fo-FO",
  "fo-FO": "fo-FO",
  fr: "fr-FR",
  "fr-BE": "fr-BE",
  "fr-CA": "fr-CA",
  "fr-CH": "fr-CH",
  "fr-FR": "fr-FR",
  fy: "fy-NL",
  "fy-NL": "fy-NL",
  ga: "ga-IE",
  "ga-IE": "ga-IE",
  gd: "gd-GB",
  "gd-GB": "gd-GB",
  gl: "gl-ES",
  "gl-ES": "gl-ES",
  gn: "gn-PY",
  "gn-PY": "gn-PY",
  gu: "gu-IN",
  "gu-IN": "gu-IN",
  gv: "gv-IM",
  "gv-IM": "gv-IM",
  ha: "ha-NG",
  "ha-NG": "ha-NG",
  he: "he-IL",
  "he-IL": "he-IL",
  hi: "hi-IN",
  "hi-IN": "hi-IN",
  ho: "ho-PG",
  "ho-PG": "ho-PG",
  hr: "hr-HR",
  "hr-HR": "hr-HR",
  ht: "ht-HT",
  "ht-HT": "ht-HT",
  hu: "hu-HU",
  "hu-HU": "hu-HU",
  hy: "hy-AM",
  "hy-AM": "hy-AM",
  hz: "hz-NA",
  "hz-NA": "hz-NA",
  ia: "ia-001",
  "ia-001": "ia-001",
  id: "id-ID",
  "id-ID": "id-ID",
  ie: "ie-EE",
  "ie-EE": "ie-EE",
  ig: "ig-NG",
  "ig-NG": "ig-NG",
  ii: "ii-CN",
  "ii-CN": "ii-CN",
  ik: "ik-US",
  "ik-US": "ik-US",
  io: "io-001",
  "io-001": "io-001",
  is: "is-IS",
  "is-IS": "is-IS",
  it: "it-IT",
  "it-IT": "it-IT",
  iu: "iu-CA",
  "iu-CA": "iu-CA",
  ja: "ja-JP",
  "ja-JP": "ja-JP",
  jv: "jv-ID",
  "jv-ID": "jv-ID",
  ka: "ka-GE",
  "ka-GE": "ka-GE",
  kg: "kg-CD",
  "kg-CD": "kg-CD",
  ki: "ki-KE",
  "ki-KE": "ki-KE",
  kj: "kj-NA",
  "kj-NA": "kj-NA",
  kk: "kk-KZ",
  "kk-KZ": "kk-KZ",
  kl: "kl-GL",
  "kl-GL": "kl-GL",
  km: "km-KH",
  "km-KH": "km-KH",
  kn: "kn-IN",
  "kn-IN": "kn-IN",
  ko: "ko-KR",
  "ko-KR": "ko-KR",
  kr: "kr-NG",
  "kr-NG": "kr-NG",
  ks: "ks-IN",
  "ks-IN": "ks-IN",
  ku: "ku-TR",
  "ku-TR": "ku-TR",
  kv: "kv-RU",
  "kv-RU": "kv-RU",
  kw: "kw-GB",
  "kw-GB": "kw-GB",
  ky: "ky-KG",
  "ky-KG": "ky-KG",
  la: "la-VA",
  "la-VA": "la-VA",
  lb: "lb-LU",
  "lb-LU": "lb-LU",
  lg: "lg-UG",
  "lg-UG": "lg-UG",
  li: "li-NL",
  "li-NL": "li-NL",
  ln: "ln-CD",
  "ln-CD": "ln-CD",
  lo: "lo-LA",
  "lo-LA": "lo-LA",
  lt: "lt-LT",
  "lt-LT": "lt-LT",
  lu: "lu-CD",
  "lu-CD": "lu-CD",
  lv: "lv-LV",
  "lv-LV": "lv-LV",
  mg: "mg-MG",
  "mg-MG": "mg-MG",
  mh: "mh-MH",
  "mh-MH": "mh-MH",
  mi: "mi-NZ",
  "mi-NZ": "mi-NZ",
  mk: "mk-MK",
  "mk-MK": "mk-MK",
  ml: "ml-IN",
  "ml-IN": "ml-IN",
  mn: "mn-MN",
  "mn-MN": "mn-MN",
  mr: "mr-IN",
  "mr-IN": "mr-IN",
  ms: "ms-MY",
  "ms-MY": "ms-MY",
  mt: "mt-MT",
  "mt-MT": "mt-MT",
  my: "my-MM",
  "my-MM": "my-MM",
  na: "na-NR",
  "na-NR": "na-NR",
  nb: "nb-NO",
  "nb-NO": "nb-NO",
  nd: "nd-ZW",
  "nd-ZW": "nd-ZW",
  ne: "ne-NP",
  "ne-NP": "ne-NP",
  ng: "ng-NA",
  "ng-NA": "ng-NA",
  nl: "nl-NL",
  "nl-NL": "nl-NL",
  nn: "nn-NO",
  "nn-NO": "nn-NO",
  no: "no-NO",
  "no-NO": "no-NO",
  nr: "nr-ZA",
  "nr-ZA": "nr-ZA",
  nv: "nv-US",
  "nv-US": "nv-US",
  ny: "ny-MW",
  "ny-MW": "ny-MW",
  oc: "oc-FR",
  "oc-FR": "oc-FR",
  oj: "oj-CA",
  "oj-CA": "oj-CA",
  om: "om-ET",
  "om-ET": "om-ET",
  or: "or-IN",
  "or-IN": "or-IN",
  os: "os-GE",
  "os-GE": "os-GE",
  pa: "pa-IN",
  "pa-IN": "pa-IN",
  pi: "pi-IN",
  "pi-IN": "pi-IN",
  pl: "pl-PL",
  "pl-PL": "pl-PL",
  ps: "ps-AF",
  "ps-AF": "ps-AF",
  pt: "pt-BR",
  "pt-BR": "pt-BR",
  "pt-PT": "pt-PT",
  qu: "qu-PE",
  "qu-PE": "qu-PE",
  rm: "rm-CH",
  "rm-CH": "rm-CH",
  rn: "rn-BI",
  "rn-BI": "rn-BI",
  ro: "ro-RO",
  "ro-RO": "ro-RO",
  ru: "ru-RU",
  "ru-RU": "ru-RU",
  rw: "rw-RW",
  "rw-RW": "rw-RW",
  sa: "sa-IN",
  "sa-IN": "sa-IN",
  sc: "sc-IT",
  "sc-IT": "sc-IT",
  sd: "sd-PK",
  "sd-PK": "sd-PK",
  se: "se-NO",
  "se-NO": "se-NO",
  sg: "sg-CF",
  "sg-CF": "sg-CF",
  si: "si-LK",
  "si-LK": "si-LK",
  sk: "sk-SK",
  "sk-SK": "sk-SK",
  sl: "sl-SI",
  "sl-SI": "sl-SI",
  sm: "sm-WS",
  "sm-WS": "sm-WS",
  sn: "sn-ZW",
  "sn-ZW": "sn-ZW",
  so: "so-SO",
  "so-SO": "so-SO",
  sq: "sq-AL",
  "sq-AL": "sq-AL",
  sr: "sr-RS",
  "sr-RS": "sr-RS",
  ss: "ss-ZA",
  "ss-ZA": "ss-ZA",
  st: "st-ZA",
  "st-ZA": "st-ZA",
  su: "su-ID",
  "su-ID": "su-ID",
  sv: "sv-SE",
  "sv-SE": "sv-SE",
  sw: "sw-TZ",
  "sw-TZ": "sw-TZ",
  ta: "ta-IN",
  "ta-IN": "ta-IN",
  te: "te-IN",
  "te-IN": "te-IN",
  tg: "tg-TJ",
  "tg-TJ": "tg-TJ",
  th: "th-TH",
  "th-TH": "th-TH",
  ti: "ti-ET",
  "ti-ET": "ti-ET",
  tk: "tk-TM",
  "tk-TM": "tk-TM",
  tl: "fil-PH",
  tn: "tn-ZA",
  "tn-ZA": "tn-ZA",
  to: "to-TO",
  "to-TO": "to-TO",
  tr: "tr-TR",
  "tr-TR": "tr-TR",
  ts: "ts-ZA",
  "ts-ZA": "ts-ZA",
  tt: "tt-RU",
  "tt-RU": "tt-RU",
  tw: "ak-GH",
  ty: "ty-PF",
  "ty-PF": "ty-PF",
  ug: "ug-CN",
  "ug-CN": "ug-CN",
  uk: "uk-UA",
  "uk-UA": "uk-UA",
  ur: "ur-PK",
  "ur-PK": "ur-PK",
  uz: "uz-UZ",
  "uz-UZ": "uz-UZ",
  ve: "ve-ZA",
  "ve-ZA": "ve-ZA",
  vi: "vi-VN",
  "vi-VN": "vi-VN",
  vo: "vo-001",
  "vo-001": "vo-001",
  wa: "wa-BE",
  "wa-BE": "wa-BE",
  wo: "wo-SN",
  "wo-SN": "wo-SN",
  xh: "xh-ZA",
  "xh-ZA": "xh-ZA",
  yi: "yi-UA",
  "yi-UA": "yi-UA",
  yo: "yo-NG",
  "yo-NG": "yo-NG",
  za: "za-CN",
  "za-CN": "za-CN",
  zh: "zh-Hans-CN",
  "zh-CN": "zh-Hans-CN",
  "zh-Hans": "zh-Hans-CN",
  "zh-Hans-CN": "zh-Hans-CN",
  "zh-Hant": "zh-Hant-TW",
  "zh-Hant-HK": "zh-Hant-HK",
  "zh-Hant-TW": "zh-Hant-TW",
  "zh-HK": "zh-Hant-HK",
  "zh-TW": "zh-Hant-TW",
  zu: "zu-ZA",
  "zu-ZA": "zu-ZA",
};

/** The distinct canonical tags (deduped), e.g. for an allowlist or the picker. */
export const CANONICAL_LANGUAGE_CODES: readonly string[] = [
  "aa-ET",
  "ab-GE",
  "ae-IR",
  "af-ZA",
  "ak-GH",
  "am-ET",
  "an-ES",
  "ar-AE",
  "ar-EG",
  "ar-MA",
  "ar-SA",
  "as-IN",
  "av-RU",
  "ay-BO",
  "az-AZ",
  "ba-RU",
  "be-BY",
  "bg-BG",
  "bho-IN",
  "bi-VU",
  "bm-ML",
  "bn-BD",
  "bo-CN",
  "br-FR",
  "bs-BA",
  "ca-ES",
  "ce-RU",
  "ch-GU",
  "co-FR",
  "cr-CA",
  "cs-CZ",
  "cu-RU",
  "cv-RU",
  "cy-GB",
  "da-DK",
  "de-AT",
  "de-CH",
  "de-DE",
  "dv-MV",
  "dz-BT",
  "ee-GH",
  "el-GR",
  "en-AU",
  "en-CA",
  "en-GB",
  "en-IE",
  "en-US",
  "eo-001",
  "es-AR",
  "es-CL",
  "es-CO",
  "es-ES",
  "es-MX",
  "es-PE",
  "es-VE",
  "et-EE",
  "eu-ES",
  "fa-IR",
  "ff-SN",
  "fi-FI",
  "fil-PH",
  "fj-FJ",
  "fo-FO",
  "fr-BE",
  "fr-CA",
  "fr-CH",
  "fr-FR",
  "fy-NL",
  "ga-IE",
  "gd-GB",
  "gl-ES",
  "gn-PY",
  "gu-IN",
  "gv-IM",
  "ha-NG",
  "he-IL",
  "hi-IN",
  "ho-PG",
  "hr-HR",
  "ht-HT",
  "hu-HU",
  "hy-AM",
  "hz-NA",
  "ia-001",
  "id-ID",
  "ie-EE",
  "ig-NG",
  "ii-CN",
  "ik-US",
  "io-001",
  "is-IS",
  "it-IT",
  "iu-CA",
  "ja-JP",
  "jv-ID",
  "ka-GE",
  "kg-CD",
  "ki-KE",
  "kj-NA",
  "kk-KZ",
  "kl-GL",
  "km-KH",
  "kn-IN",
  "ko-KR",
  "kr-NG",
  "ks-IN",
  "ku-TR",
  "kv-RU",
  "kw-GB",
  "ky-KG",
  "la-VA",
  "lb-LU",
  "lg-UG",
  "li-NL",
  "ln-CD",
  "lo-LA",
  "lt-LT",
  "lu-CD",
  "lv-LV",
  "mg-MG",
  "mh-MH",
  "mi-NZ",
  "mk-MK",
  "ml-IN",
  "mn-MN",
  "mr-IN",
  "ms-MY",
  "mt-MT",
  "my-MM",
  "na-NR",
  "nb-NO",
  "nd-ZW",
  "ne-NP",
  "ng-NA",
  "nl-NL",
  "nn-NO",
  "no-NO",
  "nr-ZA",
  "nv-US",
  "ny-MW",
  "oc-FR",
  "oj-CA",
  "om-ET",
  "or-IN",
  "os-GE",
  "pa-IN",
  "pi-IN",
  "pl-PL",
  "ps-AF",
  "pt-BR",
  "pt-PT",
  "qu-PE",
  "rm-CH",
  "rn-BI",
  "ro-RO",
  "ru-RU",
  "rw-RW",
  "sa-IN",
  "sc-IT",
  "sd-PK",
  "se-NO",
  "sg-CF",
  "si-LK",
  "sk-SK",
  "sl-SI",
  "sm-WS",
  "sn-ZW",
  "so-SO",
  "sq-AL",
  "sr-RS",
  "ss-ZA",
  "st-ZA",
  "su-ID",
  "sv-SE",
  "sw-TZ",
  "ta-IN",
  "te-IN",
  "tg-TJ",
  "th-TH",
  "ti-ET",
  "tk-TM",
  "tn-ZA",
  "to-TO",
  "tr-TR",
  "ts-ZA",
  "tt-RU",
  "ty-PF",
  "ug-CN",
  "uk-UA",
  "ur-PK",
  "uz-UZ",
  "ve-ZA",
  "vi-VN",
  "vo-001",
  "wa-BE",
  "wo-SN",
  "xh-ZA",
  "yi-UA",
  "yo-NG",
  "za-CN",
  "zh-Hans-CN",
  "zh-Hant-HK",
  "zh-Hant-TW",
  "zu-ZA",
];

/** Languages whose script subtag is meaningful and must be preserved (Chinese Simplified/Traditional). */
const SCRIPT_SIGNIFICANT_LANGUAGES = new Set<string>(["zh"]);

/**
 * Resolve the canonical region-tagged BCP-47 tag for a code unknown to {@link LANGUAGE_CANONICAL_MAP}, using CLDR
 * likely-subtags. Returns null when the input can't be parsed into a language + region.
 */
function canonicalizeViaCldr(code: string): string | null {
  try {
    const locale = new Intl.Locale(code);
    const maximized = locale.maximize();
    const language = locale.language;
    if (!language) return null;
    const region = locale.region ?? maximized.region ?? null;
    if (!region) return null;
    const keepScript = Boolean(locale.script) || SCRIPT_SIGNIFICANT_LANGUAGES.has(language);
    const script = keepScript ? (locale.script ?? maximized.script ?? null) : null;
    return [language, script, region].filter(Boolean).join("-");
  } catch {
    return null;
  }
}

/**
 * Case-insensitive index over {@link LANGUAGE_CANONICAL_MAP}. The map keys keep canonical BCP-47 casing
 * (`pt-BR`, `zh-Hans-CN`, `tl`), so a case-variant input (`TL`, `PT-br`) would miss the exact lookup and
 * leak to the CLDR fallback. Lowercasing both sides lets every casing resolve from the static map
 * deterministically, regardless of the runtime's ICU/CLDR data.
 */
const LANGUAGE_CANONICAL_MAP_BY_LOWERCASE = new Map<string, string>();
for (const [code, canonicalCode] of Object.entries(LANGUAGE_CANONICAL_MAP)) {
  LANGUAGE_CANONICAL_MAP_BY_LOWERCASE.set(code.toLowerCase(), canonicalCode);
}

/**
 * Normalize any incoming language code — a legacy bare code, mixed case, `_` separators, a URL
 * `?lang=` value, or an SDK-persisted value — to its canonical region-tagged BCP-47 tag. Returns null when the
 * input can't be resolved. The static map is consulted first (deterministic and version-independent),
 * case-insensitively; CLDR is the fallback for anything unrecognized.
 */
export function normalizeLanguageCode(input: string | null | undefined): string | null {
  if (!input) return null;
  const cleaned = input.trim().replace(/_/g, "-");
  if (cleaned.length === 0) return null;
  const exactMatch = LANGUAGE_CANONICAL_MAP[cleaned];
  if (exactMatch) return exactMatch;
  const caseInsensitiveMatch = LANGUAGE_CANONICAL_MAP_BY_LOWERCASE.get(cleaned.toLowerCase());
  if (caseInsensitiveMatch) return caseInsensitiveMatch;
  return canonicalizeViaCldr(cleaned);
}
